import React, { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "./api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

// LocalStorage keys usati per il flusso "Accedi come" (impersonation)
const K_TOKEN = "rxcasa_token";
const K_ROLE = "rxcasa_role";
const K_IMP = "rxcasa_impersonating"; // JSON: {by, agency_name} presente solo durante l'impersonation
const K_ADMIN_TOKEN_BACKUP = "rxcasa_admin_token_backup";
const K_ADMIN_ROLE_BACKUP = "rxcasa_admin_role_backup";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_IMP) || "null"); } catch { return null; }
  });

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem(K_TOKEN);
      const role = localStorage.getItem(K_ROLE);
      if (!token) { setLoading(false); return; }
      try {
        if (role === "agency") {
          const { data } = await api.get("/agency/me");
          setUser(data.user); setAgency(data.agency);
          if (data.impersonated_by && !localStorage.getItem(K_IMP)) {
            // Sync flag da server nel raro caso in cui il client abbia perso lo stato
            const imp = { by: data.impersonated_by, agency_name: data.agency?.name };
            localStorage.setItem(K_IMP, JSON.stringify(imp));
            setImpersonating(imp);
          }
        } else {
          const { data } = await api.get("/auth/me");
          setUser(data);
        }
      } catch {
        localStorage.removeItem(K_TOKEN);
        localStorage.removeItem(K_ROLE);
      }
      setLoading(false);
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(K_TOKEN, data.token);
      localStorage.setItem(K_ROLE, "staff");
      setUser(data.user); setAgency(null);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const loginAgency = async (email, password) => {
    try {
      const { data } = await api.post("/agency/auth/login", { email, password });
      localStorage.setItem(K_TOKEN, data.token);
      localStorage.setItem(K_ROLE, "agency");
      setUser(data.user); setAgency(data.agency);
      return { ok: true, must_change_password: !!data.user?.must_change_password };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const refreshMe = async () => {
    try {
      const { data } = await api.get("/agency/me");
      setUser(data.user); setAgency(data.agency);
    } catch { /* ignore */ }
  };

  const impersonateAgency = async (agencyId, agentUserId = null) => {
    try {
      // Salva backup della sessione admin corrente prima di sostituirla
      const curToken = localStorage.getItem(K_TOKEN);
      const curRole = localStorage.getItem(K_ROLE);
      if (curToken && curRole === "staff") {
        localStorage.setItem(K_ADMIN_TOKEN_BACKUP, curToken);
        localStorage.setItem(K_ADMIN_ROLE_BACKUP, curRole);
      }
      const url = agentUserId
        ? `/agencies/${agencyId}/users/${agentUserId}/impersonate`
        : `/agencies/${agencyId}/impersonate`;
      const { data } = await api.post(url);
      localStorage.setItem(K_TOKEN, data.token);
      localStorage.setItem(K_ROLE, "agency");
      const imp = {
        by: data.impersonated_by,
        agency_name: data.agency?.name,
        agent_name: data.user?.name,
        agent_email: data.user?.email,
      };
      localStorage.setItem(K_IMP, JSON.stringify(imp));
      setImpersonating(imp);
      setUser(data.user); setAgency(data.agency);
      return { ok: true };
    } catch (e) {
      localStorage.removeItem(K_ADMIN_TOKEN_BACKUP);
      localStorage.removeItem(K_ADMIN_ROLE_BACKUP);
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const stopImpersonating = async () => {
    const adminToken = localStorage.getItem(K_ADMIN_TOKEN_BACKUP);
    const adminRole = localStorage.getItem(K_ADMIN_ROLE_BACKUP);
    localStorage.removeItem(K_IMP);
    setImpersonating(null);
    if (adminToken && adminRole === "staff") {
      localStorage.setItem(K_TOKEN, adminToken);
      localStorage.setItem(K_ROLE, adminRole);
      localStorage.removeItem(K_ADMIN_TOKEN_BACKUP);
      localStorage.removeItem(K_ADMIN_ROLE_BACKUP);
      try {
        const { data } = await api.get("/auth/me");
        setUser(data); setAgency(null);
      } catch { /* ignore */ }
      return { ok: true, redirect: "/admin/agenzie" };
    }
    // fallback: logout completo
    localStorage.removeItem(K_TOKEN);
    localStorage.removeItem(K_ROLE);
    setUser(null); setAgency(null);
    return { ok: true, redirect: "/admin/login" };
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem(K_TOKEN);
    localStorage.removeItem(K_ROLE);
    localStorage.removeItem(K_IMP);
    localStorage.removeItem(K_ADMIN_TOKEN_BACKUP);
    localStorage.removeItem(K_ADMIN_ROLE_BACKUP);
    setUser(null); setAgency(null); setImpersonating(null);
  };

  return (
    <AuthCtx.Provider value={{
      user, agency, loading, impersonating,
      login, loginAgency, logout, refreshMe,
      impersonateAgency, stopImpersonating,
    }}>{children}</AuthCtx.Provider>
  );
}
